/**
 * @fileoverview Two-factor verification mutation hook.
 * @description Wraps the MFA-verify API call in a TanStack mutation for loading/error state. The
 * verify response carries the access token and session timing (no refresh token — that's an
 * httpOnly cookie now — and no profile fields), so on success this stores the access token first
 * (setAccessToken, so it's attached to the very next request), then follows up with a GET
 * /identity/me to fetch email/display name (plus role/privileges, which only the mock API serves so
 * far), then commits the full AuthUser (setUser, which is what actually flips isAuthenticated).
 * mustChangePassword is true if either response says so. Once signed in: clears the pending
 * challenge, and navigates to `resolveSignedInDestination` — completing what useLogin started. This must resolve
 * the exact same destination TwoFactorRoute's own isAuthenticated redirect does: that guard
 * re-renders the instant setUser flips isAuthenticated true, often before this hook's own navigate
 * below has committed, so if the two ever disagreed, whichever wins that race could bounce a
 * mustChangePassword user straight past /change-password. Also tracks a "login" analytics event,
 * since this is the point sign-in actually completes.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { CURRENT_SESSION_QUERY_KEY } from '@/features/auth/hooks/useCurrentSession'
import { getCurrentPrincipal } from '@/features/auth/services/session.service'
import {
  verifyTwoFactorCode,
  type SessionResponse,
  type VerifyTwoFactorRequest,
} from '@/features/auth/services/two-factor.service'
import { trackEvent } from '@/shared/lib/analytics/analytics.service'
import { resolveSignedInDestination } from '@/shared/lib/auth/resolve-home-route'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { AuthUser } from '@/shared/lib/auth/auth.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Submits the entered verification code and, on success, stores the access token,
 * fetches the caller's profile, clears the pending challenge, and redirects to /change-password
 * (if the password must be changed) or the dashboard.
 * @returns {ReturnType<typeof useMutation<SessionResponse, ApiError, VerifyTwoFactorRequest>>} The
 * mutation object (mutate, isPending, error, ...).
 */
export function useVerifyTwoFactor() {
  const { setAccessToken, setUser } = useAuth()
  const { challenge, clearChallenge } = useTwoFactorChallenge()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation<SessionResponse, ApiError, VerifyTwoFactorRequest>({
    mutationFn: verifyTwoFactorCode,
    onSuccess: async (session) => {
      setAccessToken(session.accessToken)

      const principal = await getCurrentPrincipal()
      // Seed useCurrentSession's cache, so the dashboard doesn't fetch the same principal again.
      queryClient.setQueryData(CURRENT_SESSION_QUERY_KEY, principal)
      const user: AuthUser = {
        email: principal.email || (challenge?.email ?? ''),
        ...(principal.displayName && { name: principal.displayName }),
        activeRoleKey: principal.activeRoleKey ?? '',
        activeRoleName: principal.activeRoleName ?? '',
        privileges: principal.privileges ?? [],
        mustChangePassword: session.mustChangePassword || principal.mustChangePassword,
      }

      setUser(user)
      clearChallenge()
      void trackEvent('login', { method: 'password_and_otp' })
      void navigate(resolveSignedInDestination(user), { replace: true })
    },
  })
}
